import { formatDateTime } from "@/shared/lib/date-time";
import { cn } from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import {
  BanIcon,
  BanknoteIcon,
  CheckCircleIcon,
  CircleDotIcon,
  LockIcon,
  SendIcon,
  XCircleIcon,
} from "lucide-react";
import type { ExpenseRecordDetailDTO } from "../api/client";
import { EXPENSE_PAYMENT_METHOD_LABELS } from "../lib/expense-format";

type TimelineTone = "done" | "current" | "destructive" | "muted";

type TimelineItem = {
  id: string;
  title: string;
  detail?: string;
  time?: string;
  tone: TimelineTone;
  icon: LucideIcon;
};

const toneStyles: Record<
  TimelineTone,
  { dot: string; icon: string; title: string }
> = {
  done: {
    dot: "bg-emerald-500/15",
    icon: "text-emerald-700 dark:text-emerald-400",
    title: "text-foreground",
  },
  current: {
    dot: "bg-primary/15",
    icon: "text-primary",
    title: "text-foreground",
  },
  destructive: {
    dot: "bg-destructive/15",
    icon: "text-destructive",
    title: "text-foreground",
  },
  muted: {
    dot: "bg-muted",
    icon: "text-muted-foreground",
    title: "text-muted-foreground",
  },
};

function buildTimelineItems(record: ExpenseRecordDetailDTO): TimelineItem[] {
  const items: TimelineItem[] = [
    {
      id: "created",
      title: "ສ້າງລາຍການ",
      detail: record.createdByName ?? record.createdBy,
      time: formatDateTime(record.createdAt),
      tone: "done",
      icon: CircleDotIcon,
    },
  ];

  if (record.submittedAt) {
    items.push({
      id: "submitted",
      title: "ສົ່ງອະນຸມັດ",
      time: formatDateTime(record.submittedAt),
      tone: "done",
      icon: SendIcon,
    });
  } else if (["draft", "rejected"].includes(record.status)) {
    items.push({
      id: "submitted-pending",
      title: "ລໍຖ້າສົ່ງອະນຸມັດ",
      tone: record.status === "draft" ? "current" : "muted",
      icon: SendIcon,
    });
  }

  if (record.approvedAt) {
    items.push({
      id: "approved",
      title: "ອະນຸມັດ",
      detail: record.approvedByName ?? undefined,
      time: formatDateTime(record.approvedAt),
      tone: "done",
      icon: CheckCircleIcon,
    });
  } else if (record.rejectedAt) {
    items.push({
      id: "rejected",
      title: "ປະຕິເສດ",
      detail: record.rejectionReason ?? record.rejectedByName ?? undefined,
      time: formatDateTime(record.rejectedAt),
      tone: "destructive",
      icon: XCircleIcon,
    });
  } else if (record.status === "submitted") {
    items.push({
      id: "approved-pending",
      title: "ລໍຖ້າອະນຸມັດ",
      tone: "current",
      icon: CheckCircleIcon,
    });
  }

  if (record.paidAt) {
    items.push({
      id: "paid",
      title: "ຈ່າຍແລ້ວ",
      detail:
        EXPENSE_PAYMENT_METHOD_LABELS[record.paymentMethod ?? ""] ??
        record.paymentMethod ??
        undefined,
      time: formatDateTime(record.paidAt),
      tone: "done",
      icon: BanknoteIcon,
    });
  } else if (record.status === "approved") {
    items.push({
      id: "paid-pending",
      title: "ລໍຖ້າຈ່າຍ",
      tone: "current",
      icon: BanknoteIcon,
    });
  }

  if (record.closedAt) {
    items.push({
      id: "closed",
      title: "ປິດລາຍການ",
      detail: record.closedByName ?? undefined,
      time: formatDateTime(record.closedAt),
      tone: "done",
      icon: LockIcon,
    });
  } else if (record.status === "paid") {
    items.push({
      id: "closed-pending",
      title: "ລໍຖ້າປິດລາຍການ",
      tone: "muted",
      icon: LockIcon,
    });
  }

  if (record.voidedAt) {
    items.push({
      id: "voided",
      title: "ຍົກເລີກ",
      detail: record.voidReason ?? record.voidedByName ?? undefined,
      time: formatDateTime(record.voidedAt),
      tone: "destructive",
      icon: BanIcon,
    });
  }

  if (record.status === "cancelled") {
    items.push({
      id: "cancelled",
      title: "ຍົກເລີກ",
      tone: "muted",
      icon: BanIcon,
    });
  }

  return items;
}

type ExpenseRecordTimelineProps = {
  record: ExpenseRecordDetailDTO;
};

export function ExpenseRecordTimeline({ record }: ExpenseRecordTimelineProps) {
  const items = buildTimelineItems(record);

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const styles = toneStyles[item.tone];
        const Icon = item.icon;
        const isLast = index === items.length - 1;

        return (
          <div key={item.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-full",
                  styles.dot,
                )}
              >
                <Icon className={cn("size-4", styles.icon)} />
              </div>
              {!isLast ? (
                <div className="mt-1 w-px flex-1 bg-border" aria-hidden />
              ) : null}
            </div>

            <div className="min-w-0 pb-1">
              <p className={cn("font-medium text-sm", styles.title)}>
                {item.title}
              </p>
              {item.detail ? (
                <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
                  {item.detail}
                </p>
              ) : null}
              {item.time ? (
                <p className="mt-0.5 text-muted-foreground text-xs tabular-nums">
                  {item.time}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
